import { useApiServiceClient } from "@/modules/app/apiServicesContext";
import {
  SessionRequest,
  UserUpdateLocationRequest,
  UserUpdateLocationResponse
} from "@travello/grpc-api/js/user_pb";
import { fromUnary } from "@/utils/grpcRxjs";
import { Empty } from "google-protobuf/google/protobuf/empty_pb";
import { map, switchMap } from "rxjs/operators";
import { Optional } from "utility-types";
import { Distance, LatLong } from "@travello/grpc-api/js/common_pb";
import { useAuthState } from "@/modules/auth/useAuthState";
import { from } from "rxjs";
import { useMemo } from "react";

const useRepository = () => {
  const {
    sessionServiceClient: getSessionServiceClient,
    userEditServiceClient: getUserEditServiceClient
  } = useApiServiceClient();
  const [{ serviceMetaData }] = useAuthState();

  const session$ = ({ sessionType }: SessionRequest.AsObject) => {
    const request = new SessionRequest();
    request.setSessionType(sessionType);
    return from(getSessionServiceClient()).pipe(
      switchMap(sessionServiceClient =>
        fromUnary<Empty>(callback =>
          sessionServiceClient.session(request, serviceMetaData, callback)
        )
      ),
      map(r => {
        return;
      })
    );
  };

  const userUpdateLocation$ = ({
    latLng: { latitude, longitude },
    accuracy: { distance, distanceType = Distance.DistanceType.METERS }, // https://developer.mozilla.org/en-US/docs/Web/API/Coordinates/accuracy
    updateType = UserUpdateLocationRequest.UpdateType.FOREGROUND
  }: Pick<UserUpdateLocationRequest.AsObject, "updateType"> & {
    latLng: LatLong.AsObject;
    accuracy: Optional<Distance.AsObject, "distanceType">;
  }) => {
    const latLong = new LatLong();
    latLong.setLatitude(latitude);
    latLong.setLongitude(longitude);

    const accuracy = new Distance();
    accuracy.setDistance(distance);
    accuracy.setDistanceType(distanceType);

    const request = new UserUpdateLocationRequest();
    request.setUpdateType(updateType);
    request.setLatLng(latLong);
    request.setAccuracy(accuracy);

    return from(getUserEditServiceClient()).pipe(
      switchMap(userEditServiceClient =>
        fromUnary<UserUpdateLocationResponse>(callback =>
          userEditServiceClient.userUpdateLocation(
            request,
            serviceMetaData,
            callback
          )
        )
      ),
      map(r => r.toObject())
    );
  };

  return useMemo(
    () => ({
      session$,
      userUpdateLocation$
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceMetaData]
  );
};

export default useRepository;
